import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import taskService from '../../services/taskService';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { employeeService } from '../../services/employeeService';

const columns = [
  { id: 'todo', name: 'To Do' },
  { id: 'in_progress', name: 'In Progress' },
  { id: 'review', name: 'In Review' },
  { id: 'done', name: 'Done' },
];

const Kanban = () => {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [currentProjectId, setCurrentProjectId] = useState('');
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewTask, setShowNewTask] = useState(false);
  const [newTask, setNewTask] = useState({ title: '', description: '', priority: 'medium', dueDate: '' });
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [projectForm, setProjectForm] = useState({ name: '', description: '' });
  const [showTaskDetail, setShowTaskDetail] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [comments, setComments] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [newLabel, setNewLabel] = useState('');

  const load = async (projectId) => {
    try {
      setLoading(true);
      const p = await taskService.listProjects();
      setProjects(p.data?.projects || p.projects || []);
      const t = await taskService.listTasks(projectId ? { projectId } : {});
      setTasks(t.data?.tasks || t.tasks || []);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load tasks/projects');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(currentProjectId); }, [currentProjectId]);
  useEffect(() => { (async () => { try { const res = await employeeService.getEmployees({ page:1, limit:100 }); setEmployees(res.data?.employees || res.data || []);} catch(e){} })(); }, []);

  const grouped = useMemo(() => {
    const g = { todo: [], in_progress: [], review: [], done: [] };
    for (const task of tasks) { g[task.status]?.push(task); }
    return g;
  }, [tasks]);

  const canManageProjects = ['super_admin', 'admin', 'manager'].includes(user?.Role?.name);

  const createProject = async (e) => {
    e.preventDefault();
    try {
      await taskService.createProject(projectForm);
      setShowProjectForm(false);
      setProjectForm({ name: '', description: '' });
      load(currentProjectId);
      toast.success('Project created');
    } catch (e) {
      console.error(e);
      toast.error('Create project failed');
    }
  };

  const createTask = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...newTask, projectId: currentProjectId || undefined };
      await taskService.createTask(payload);
      setShowNewTask(false);
      setNewTask({ title: '', description: '', priority: 'medium', dueDate: '' });
      load(currentProjectId);
      toast.success('Task created');
    } catch (e) {
      console.error(e);
      toast.error('Create task failed');
    }
  };

  const changeStatus = async (task, status) => {
    try {
      await taskService.updateTask(task.id, { status });
      setTasks(prev => prev.map(t => t.id === task.id ? { ...t, status } : t));
    } catch (e) {
      toast.error('Failed to move task');
    }
  };

  const openTask = async (task) => {
    setShowTaskDetail(task);
    setCommentText('');
    setAttachments(task.Attachments || []);
    try { const res = await taskService.getComments(task.id); setComments(res.data?.comments || res.comments || []); } catch(e) { setComments([]); }
  };
  const addComment = async () => {
    try { await taskService.addComment(showTaskDetail.id, commentText); setCommentText(''); toast.success('Comment added'); load(currentProjectId); }
    catch (e) { toast.error('Add comment failed'); }
  };
  const uploadAttachment = async (e) => {
    const file = e.target.files?.[0]; if (!file) return; 
    try { await taskService.uploadAttachment(showTaskDetail.id, file); toast.success('Attachment uploaded'); load(currentProjectId); }
    catch (err) { toast.error('Upload failed'); }
  };

  const assignTask = async (userId) => {
    try { await taskService.updateTask(showTaskDetail.id, { assignedToId: userId || null }); toast.success('Assignee updated'); load(currentProjectId); }
    catch (e) { toast.error('Update assignee failed'); }
  };

  const addLabel = async () => {
    const label = (newLabel || '').trim(); if (!label) return;
    const next = Array.from(new Set([...(showTaskDetail.labels || []), label]));
    try { await taskService.updateTask(showTaskDetail.id, { labels: next }); setNewLabel(''); toast.success('Label added'); load(currentProjectId); }
    catch (e) { toast.error('Add label failed'); }
  };
  const removeLabel = async (l) => {
    const next = (showTaskDetail.labels || []).filter(x => x !== l);
    try { await taskService.updateTask(showTaskDetail.id, { labels: next }); toast.success('Label removed'); load(currentProjectId); }
    catch (e) { toast.error('Remove label failed'); }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Tasks</h1>
            <p className="text-gray-600">Kanban board and project view</p>
          </div>
          <div className="flex items-center space-x-2">
            <select className="border rounded px-3 py-2" value={currentProjectId} onChange={(e) => setCurrentProjectId(e.target.value)}>
              <option value="">All Tasks</option>
              {projects.map(p => (<option key={p.id} value={p.id}>{p.name}</option>))}
            </select>
            <button onClick={() => setShowNewTask(true)} className="px-3 py-2 bg-blue-600 text-white rounded">New Task</button>
            {canManageProjects && (
              <button onClick={() => setShowProjectForm(true)} className="px-3 py-2 bg-indigo-600 text-white rounded">New Project</button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="p-6 text-center">Loading...</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {columns.map(col => (
              <div key={col.id} className="bg-white rounded-lg shadow-sm border border-gray-200"
                   onDragOver={(e) => e.preventDefault()}
                   onDrop={(e) => { const id = e.dataTransfer.getData('taskId'); const t = tasks.find(x => String(x.id) === id); if (t && t.status !== col.id) changeStatus(t, col.id); }}>
                <div className="p-3 font-semibold border-b">{col.name} ({grouped[col.id].length})</div>
                <div className="p-3 space-y-3 min-h-[200px]">
                  {grouped[col.id].map(task => (
                    <motion.div key={task.id} className="border rounded p-3 bg-white hover:shadow cursor-pointer"
                                draggable
                                onDragStart={(e) => e.dataTransfer.setData('taskId', String(task.id))}
                                whileHover={{ scale: 1.01 }} onClick={() => openTask(task)}>
                      <div className="font-medium">{task.title}</div>
                      <div className="text-sm text-gray-500">{task.description || 'No description'}</div>
                      <div className="text-xs text-gray-400 mt-1">Priority: {task.priority} {task.dueDate ? `• Due: ${new Date(task.dueDate).toLocaleDateString()}` : ''}</div>
                      {(task.labels || []).length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {(task.labels || []).map(l => (<span key={l} className="text-[10px] px-2 py-0.5 rounded bg-gray-100 text-gray-700">{l}</span>))}
                        </div>
                      )}
                      <div className="flex space-x-2 mt-2">
                        {columns.filter(c => c.id !== task.status).map(btn => (
                          <button key={btn.id} className="text-xs px-2 py-1 border rounded" onClick={() => changeStatus(task, btn.id)}>Move to {btn.name}</button>
                        ))}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {showNewTask && (
          <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow p-6 w-full max-w-lg">
              <h2 className="text-lg font-semibold mb-4">New Task</h2>
              <form onSubmit={createTask} className="space-y-3">
                <input className="border rounded px-3 py-2 w-full" placeholder="Title" value={newTask.title} onChange={(e) => setNewTask({ ...newTask, title: e.target.value })} />
                <textarea className="border rounded px-3 py-2 w-full" rows={3} placeholder="Description" value={newTask.description} onChange={(e) => setNewTask({ ...newTask, description: e.target.value })} />
                <div className="grid grid-cols-2 gap-3">
                  <select className="border rounded px-3 py-2" value={newTask.priority} onChange={(e) => setNewTask({ ...newTask, priority: e.target.value })}>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                  <input type="date" className="border rounded px-3 py-2" value={newTask.dueDate} onChange={(e) => setNewTask({ ...newTask, dueDate: e.target.value })} />
                </div>
                <div className="flex justify-end space-x-2 pt-2">
                  <button type="button" className="px-4 py-2 border rounded" onClick={() => setShowNewTask(false)}>Cancel</button>
                  <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded">Create</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showProjectForm && (
          <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow p-6 w-full max-w-lg">
              <h2 className="text-lg font-semibold mb-4">New Project</h2>
              <form onSubmit={createProject} className="space-y-3">
                <input className="border rounded px-3 py-2 w-full" placeholder="Project Name" value={projectForm.name} onChange={(e) => setProjectForm({ ...projectForm, name: e.target.value })} />
                <textarea className="border rounded px-3 py-2 w-full" rows={3} placeholder="Description" value={projectForm.description} onChange={(e) => setProjectForm({ ...projectForm, description: e.target.value })} />
                <div className="flex justify-end space-x-2 pt-2">
                  <button type="button" className="px-4 py-2 border rounded" onClick={() => setShowProjectForm(false)}>Cancel</button>
                  <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded">Create</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showTaskDetail && (
          <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow p-6 w-full max-w-3xl">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">{showTaskDetail.title}</h2>
                <button className="text-gray-500" onClick={() => setShowTaskDetail(null)}>✕</button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="md:col-span-2 space-y-3">
                  <div className="text-sm text-gray-600">{showTaskDetail.description || 'No description'}</div>
                  <div className="text-xs text-gray-400">Priority: {showTaskDetail.priority} {showTaskDetail.dueDate ? `• Due: ${new Date(showTaskDetail.dueDate).toLocaleDateString()}` : ''}</div>
                  <div className="space-y-2">
                    <div className="font-medium">Comments</div>
                    <div className="space-y-2 max-h-48 overflow-auto">
                      {comments.map(c => (
                        <div key={c.id} className="text-sm border rounded p-2">
                          <div className="font-medium text-gray-700">{c.User?.firstName} {c.User?.lastName}</div>
                          <div>{c.comment}</div>
                        </div>
                      ))}
                    </div>
                    <div className="flex space-x-2">
                      <input className="border rounded px-3 py-2 flex-1" placeholder="Write a comment..." value={commentText} onChange={(e) => setCommentText(e.target.value)} />
                      <button className="px-3 py-2 bg-blue-600 text-white rounded" onClick={addComment}>Add</button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="font-medium">Attachments</div>
                    <div className="space-y-1">
                      {attachments.map(a => (
                        <a key={a.id} href={a.fileUrl} target="_blank" rel="noreferrer" className="block text-sm text-blue-600 hover:underline">{a.fileName}</a>
                      ))}
                    </div>
                    <input type="file" onChange={uploadAttachment} />
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <div className="font-medium mb-1">Status</div>
                    <select className="border rounded px-3 py-2 w-full" value={showTaskDetail.status} onChange={(e) => changeStatus(showTaskDetail, e.target.value)}>
                      {columns.map(c => (<option key={c.id} value={c.id}>{c.name}</option>))}
                    </select>
                  </div>
                  <div>
                    <div className="font-medium mb-1">Assignee</div>
                    <select className="border rounded px-3 py-2 w-full" value={showTaskDetail.assignedToId || ''} onChange={(e) => assignTask(e.target.value || null)}>
                      <option value="">Unassigned</option>
                      {employees.map(emp => (<option key={emp.id} value={emp.userId}>{emp.User?.firstName} {emp.User?.lastName}</option>))}
                    </select>
                  </div>
                  <div>
                    <div className="font-medium mb-1">Labels</div>
                    <div className="flex flex-wrap gap-1 mb-2">
                      {(showTaskDetail.labels || []).map(l => (
                        <span key={l} className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-700 flex items-center">
                          {l}
                          <button className="ml-1 text-red-600" onClick={() => removeLabel(l)}>×</button>
                        </span>
                      ))}
                    </div>
                    <div className="flex space-x-2">
                      <input className="border rounded px-2 py-1 flex-1" placeholder="New label" value={newLabel} onChange={(e) => setNewLabel(e.target.value)} />
                      <button className="px-3 py-1 bg-gray-800 text-white rounded" onClick={addLabel}>Add</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Kanban;


